import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Trophy, Users, Activity, Award, MapPin, Shield, Swords, HelpCircle, LogIn,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

const navItems = [
  { icon: Activity, label: 'Matches', path: '/matches', desc: 'View live and completed matches' },
  { icon: Users, label: 'Players', path: '/players', desc: 'Browse player profiles and stats' },
  { icon: Shield, label: 'Clubs', path: '/clubs', desc: 'Explore badminton clubs near you' },
  { icon: Users, label: 'Teams', path: '/teams', desc: 'Join or create teams' },
  { icon: Trophy, label: 'Tournaments', path: '/tournaments', desc: 'Upcoming and live tournaments' },
  { icon: Award, label: 'Rankings', path: '/rankings', desc: 'Leaderboards and player rankings' },
  { icon: MapPin, label: 'Venues', path: '/venues', desc: 'Find badminton courts' },
  { icon: HelpCircle, label: 'Support', path: '/support', desc: 'Get help and submit queries' },
];

export default function Browse() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  const handleNavClick = (path) => {
    navigate('/login', { state: { redirectTo: path, message: 'Sign in to access this section' } });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="browse-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="LoveAll" className="h-8 w-8 rounded-md object-cover" />
            <span className="font-heading text-xl font-bold uppercase tracking-tight">LoveAll</span>
          </Link>
          <div className="hidden md:flex items-center gap-1">
            {navItems.slice(0, 6).map(item => (
              <button key={item.path} onClick={() => handleNavClick(item.path)} className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/10 rounded-md transition-colors">
                {item.label}
              </button>
            ))}
          </div>
          <Button onClick={() => navigate('/login')} className="bg-secondary hover:bg-secondary/90" data-testid="browse-login-btn">
            <LogIn className="mr-2 h-4 w-4" />Sign In
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mb-3">Explore LoveAll</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">Sign in to access matches, tournaments, player stats, and more. Here's what's waiting for you:</p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <Card key={item.path} className="group cursor-pointer hover:border-primary/50 transition-all" onClick={() => handleNavClick(item.path)} data-testid={`browse-card-${item.label.toLowerCase()}`}>
                <CardContent className="p-6 text-center">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-sm uppercase tracking-tight mb-1">{item.label}</h3>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">Ready to get started?</p>
          <Button size="lg" onClick={() => navigate('/login')} className="bg-secondary hover:bg-secondary/90 font-heading text-lg uppercase px-8" data-testid="browse-signup-btn">
            <LogIn className="mr-2 h-5 w-5" />Sign In / Register
          </Button>
        </div>
      </main>
    </div>
  );
}
